import { Badge } from '@components/common/Badge';
import { Table } from '@radix-ui/themes';
import { RxCheck } from 'react-icons/rx';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { cn } from '@helpers/styleUtils';
// import { LuPen } from 'react-icons/lu';
import { MdOutlineFileDownload } from 'react-icons/md';
import { generateDocx } from '@utils/documentUtils';
import { HoverableTableSkeleton } from '@components/loaders/SkeletonLoaders';
import useJobDescriptionsStore from '@stores/jobDescriptionsStore';
import { ScrollArea } from '@components/common/ScrollArea';
import { DATA_STORE_KEYS, DataStore } from '@providers/dataStore';

export const JobDescriptionsTable = memo(
    ({
        hideAction,
        hideSelector,
        jobDescriptions,
        loading,
        jobDescriptionDataSelected,
        setJobDescriptionDataSelected,
        showDetailedView,
        isDetailedView,
        setActionLoading,
        // Remove handleApprove from props since we'll handle it directly
    }) => {
        const { approveJobDescription } = useJobDescriptionsStore();

        const handleApproveClick = async (e, item) => {
            e.stopPropagation();
            setActionLoading(true);
            try {
                await approveJobDescription(item);
                // Open the detailed view after approval
                setJobDescriptionDataSelected(item);
                showDetailedView && showDetailedView(true);
            } catch (error) {
                console.error('Error approving job description:', error);
            } finally {
                setActionLoading(false);
            }
        };

        if (loading) {
            return <HoverableTableSkeleton rows={4} columns={4} showHeader={true} />;
        }

        if (jobDescriptions && !jobDescriptions.length > 0) {
            return (
                <p className="text-sm font-semibold block w-full text-center">
                    No job Descriptions found.
                </p>
            );
        }

        const getStatusLabel = status => {
            if (status.toLowerCase() === 'pending') {
                return <Badge variant="default">Reviewed</Badge>;
            }

            if (status.toLowerCase() === 'approved') {
                return <Badge variant="success">Approved</Badge>;
            }
            if (status.toLowerCase() === 'cancel' || status.toLowerCase() === 'rejected') {
                return <Badge variant="danger">Rejected</Badge>;
            }
            return <Badge variant="warning">{status}</Badge>;
        };

        const isWfm = DataStore.getItem(DATA_STORE_KEYS.USER_CURRENT_ROLE).toLowerCase() === 'wfm';

        return (
            <div className="overflow-x-auto table-auto w-full border border-gray-200 bg-white rounded-[10px]">
                <ScrollArea className="h-[400px] ">
                    <Table.Root layout="auto">
                        <Table.Header className="bg-white w-full">
                            <Table.Row className="text-sm font-semibold border-b border-gray-200">
                                {!hideSelector && (
                                    <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                        <input className="w-full h-full" type="checkbox" />
                                    </Table.ColumnHeaderCell>
                                )}
                                <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                    #
                                </Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                    Job Title:
                                </Table.ColumnHeaderCell>
                                {/* <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                Description:
                            </Table.ColumnHeaderCell> */}
                                <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                    Job Request Reviewed By:
                                </Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left min-w-24">
                                    Status:
                                </Table.ColumnHeaderCell>
                                {!hideAction && (
                                    <Table.ColumnHeaderCell className="p-3 text-left min-w-24"></Table.ColumnHeaderCell>
                                )}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body className="divide-y divide-gray-200">
                            {jobDescriptions &&
                                jobDescriptions.map((item, index) => (
                                    <Table.Row
                                        key={item?.ID || item?.id || index}
                                        className={cn(
                                            'hover:bg-slate-100 bg-white cursor-pointer',
                                            jobDescriptionDataSelected &&
                                                jobDescriptionDataSelected.ID === item.ID
                                                ? 'bg-slate-100'
                                                : '',
                                        )}
                                        onClick={() => {
                                            setJobDescriptionDataSelected(item);
                                            isDetailedView && showDetailedView(true);
                                        }}
                                    >
                                        {!hideSelector && (
                                            <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                                <input className="w-full h-full" type="checkbox" />
                                            </Table.ColumnHeaderCell>
                                        )}
                                        <Table.RowHeaderCell className="border-r border-gray-200 p-3 text-left text-sm font-semibold">
                                            {index + 1}
                                        </Table.RowHeaderCell>
                                        <Table.RowHeaderCell className="border-r border-gray-200 p-3 text-left text-sm font-semibold">
                                            {item.position_required}
                                        </Table.RowHeaderCell>
                                        {/* <Table.Cell className="border-r border-gray-200 p-3 text-left text-xs text-gray-600 whitespace-pre-line">
                                        {parse(
                                            item.job_description
                                                ? item.job_description.slice(0, 100)
                                                : '',
                                        )}
                                    </Table.Cell> */}
                                        <Table.Cell className="border-r border-gray-200 p-3 text-left text-sm ">
                                            {item?.emp_name || '--'}
                                        </Table.Cell>
                                        <Table.Cell className="border-r border-gray-200 p-3 text-left">
                                            {getStatusLabel(item?.jd_status)}
                                        </Table.Cell>

                                        {!hideAction && (
                                            <Table.Cell className="flex items-center justify-start gap-2 p-3 text-left text-sm ">
                                                {item.jd_status === 'Pending' && !isWfm && (
                                                    <div
                                                        onClick={e => handleApproveClick(e, item)}
                                                        className="rounded-full px-2 py-1 bg-green-600 text-white flex items-center justify-center gap-1"
                                                    >
                                                        <RxCheck className="text-lg" /> Approve
                                                    </div>
                                                )}
                                                {(!isWfm ||
                                                    (isWfm && item.jd_status === 'Approved')) && (
                                                    <div
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            generateDocx(
                                                                item.jd_Job_description,
                                                                `JD-${item.position_required}`,
                                                            );
                                                        }}
                                                        className="rounded-full px-2 py-1 bg-gray-600 text-white flex items-center justify-center gap-1"
                                                    >
                                                        <MdOutlineFileDownload className="text-xl" />
                                                    </div>
                                                )}
                                                {/* <div
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleReject();
                                                }}
                                                className="rounded-full px-2 py-1 bg-primary-blue text-white flex items-center justify-center gap-1"
                                            >
                                                <LuPen className="text-lg" />
                                            </div> */}
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                ))}
                        </Table.Body>
                    </Table.Root>
                </ScrollArea>
            </div>
        );
    },
);

JobDescriptionsTable.displayName = 'JobDescriptionsTable';

JobDescriptionsTable.propTypes = {
    setActionLoading: PropTypes.func,
    hideAction: PropTypes.bool,
    showDetailedView: PropTypes.func,
    isDetailedView: PropTypes.bool,
    hideSelector: PropTypes.bool,
    // Remove handleApprove from PropTypes
    handleReject: PropTypes.func,
    jobDescriptions: PropTypes.arrayOf(
        PropTypes.shape({
            ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            position_required: PropTypes.string.isRequired,
            job_description: PropTypes.string.isRequired,
            status: PropTypes.string.isRequired,
            created_at: PropTypes.string.isRequired,
        }),
    ).isRequired,
    loading: PropTypes.bool,
    jobDescriptionDataSelected: PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
    setJobDescriptionDataSelected: PropTypes.func,
};
